import { Box } from "@chakra-ui/react";
import {
  BaseStack,
  baseColors,
  BaseButton,
  ColorIcon,
  Spacing,
  Image,
  getCdnImageSrc,
  customEvent,
} from "goi_common";
import { ReactNode } from "react";

interface MatchRequestOnboardingProps {
  /** CTA 클릭 시 지역 선택 스텝으로 진행 */
  onNext: () => void;
  /** 비용 퍼널 외에도 같은 인트로 레이아웃을 쓸 수 있도록 내용만 주입한다. */
  title?: ReactNode;
  description?: ReactNode;
  imageSrc?: string;
  imageAlt?: string;
  footnote?: ReactNode;
  ctaLabel?: string;
  trackDefaultNextEvent?: boolean;
  iconName?: React.ComponentProps<typeof ColorIcon>["name"];
  footnoteSpacing?: number;
  /**
   * 기본값 없음(전폭)이 `?template=cost` 의 현행 동작이다. 480 으로 조이는 것은
   * 장지 추천 인트로처럼 자체 헤더가 480 중앙정렬인 화면만 넘긴다.
   * 공유 컴포넌트에 기본값으로 박으면 이번 기획 범위 밖인 비용 퍼널 데스크톱까지 바뀐다.
   */
  maxWidth?: string;
}

/**
 * 실험 #312(B안) 온보딩 화면.
 * A안의 첫문항("걱정되는 부분")을 대체하는 화면으로, CTA 클릭 시 지역 선택 스텝으로 넘어간다.
 * 순수 프레젠테이션 컴포넌트 — 스텝 전환은 MatchRequestOnboardingFlow가 관리.
 */
export default function MatchRequestOnboarding({
  onNext,
  title = (
    <>
      내 상황에 맞는
      <br />
      장례 비용, 얼마나 들까요?
    </>
  ),
  description = (
    <>
      지역과 상황에 따라 비용이 크게 달라요.
      <br />
      몇 가지만 선택하면 예상 비용과
      <br />
      줄일 수 있는 항목까지 알려드릴게요.
    </>
  ),
  imageSrc = "direct/registration/post-signup/content-guide.png",
  imageAlt = "장례 예상 비용 안내",
  footnote = "30초면 전체 견적을 받아볼 수 있어요.",
  ctaLabel = "무료로 예상 비용 확인하기",
  trackDefaultNextEvent = true,
  iconName,
  footnoteSpacing = 44,
  maxWidth,
}: MatchRequestOnboardingProps) {
  const handleNext = () => {
    // 온보딩 → 지역선택 진행. 온보딩 이탈률 측정용(exposure와 별개).
    if (trackDefaultNextEvent) {
      customEvent({
        newGtm: { path: "match_request", trigger_and_target: "onboarding-next" },
      });
    }
    onNext();
  };

  return (
    <BaseStack
      width="100%"
      maxWidth={maxWidth}
      margin="0 auto"
      alignItems="center"
      px={20}
      pb={160}
    >
      <Spacing gap={56} />

      <Box
        className="title_3_sb"
        color={baseColors.label_normal}
        textAlign="center"
        whiteSpace="pre-line"
      >
        {title}
      </Box>

      <Spacing gap={16} />

      <Box className="body_1_normal_r" color={baseColors.label_alternative_1} textAlign="center">
        {description}
      </Box>

      <Spacing gap={40} />

      {iconName ? (
        <Box height="200px" display="flex" alignItems="center" justifyContent="center">
          <ColorIcon name={iconName} size={128} />
        </Box>
      ) : (
        <Image src={getCdnImageSrc(imageSrc)} alt={imageAlt} width="100%" />
      )}

      {footnote && (
        <>
          <Spacing gap={footnoteSpacing} />
          <Box className="caption_1_r" color={baseColors.label_alternative_1} textAlign="center">
            {footnote}
          </Box>
        </>
      )}

      <Spacing gap={16} />

      {/* CTA — 견적 플로우처럼 콘텐츠 흐름 내부에 배치(fixed 아님) */}
      <Box width="100%" cursor="pointer">
        <BaseButton type="primary" size="large" onClick={handleNext}>
          <Box className="body_1_normal_m">{ctaLabel}</Box>
        </BaseButton>
      </Box>
    </BaseStack>
  );
}
